import { MRT_ColumnFiltersState } from 'mantine-react-table';
import {
  formatDateFilterToken,
  isDateRangeFilterID,
  toISODateTime,
} from 'utils/dateUtils';

type QueryLikeFilter = { id: string; value: unknown };

// These columns are represented as a single `start~end` token in the query string
// to match backend filter expectations.
const parseDateRangeTokenValue = (value: string): [string, string] | null => {
  const [startRaw, endRaw, ...rest] = value.split('~');
  if (rest.length > 0) return null;

  const start = startRaw?.trim();
  const end = endRaw?.trim();
  if (!start || !end) return null;

  return [start, end];
};

const parseSingleFilter = (
  filterToken: string
): { id: string; value: unknown } | null => {
  const [idRaw, valueRaw] = filterToken.split(':');
  if (!idRaw || valueRaw == null) return null;

  const id = idRaw.trim();
  const value = valueRaw.trim();
  if (!id) return null;

  // Date-range filters deserialize into tuple-like arrays so MRT date-range
  // columns can consume them directly.
  if (isDateRangeFilterID(id) && value.includes('~')) {
    const parsedDateRange = parseDateRangeTokenValue(value);
    if (!parsedDateRange) return null;
    return { id, value: parsedDateRange };
  }

  return { id, value };
};

export const parseClaimFiltersQueryString = (
  filtersQuery: string
): MRT_ColumnFiltersState => {
  if (!filtersQuery.trim()) return [];

  return filtersQuery
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .map(parseSingleFilter)
    .filter((filter): filter is { id: string; value: unknown } =>
      Boolean(filter)
    );
};

const normalizeQueryFilterTokenValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return toISODateTime(value, { zone: 'utc' });
  if (typeof value === 'string') {
    const normalizedValue = value.trim();
    return normalizedValue ? normalizedValue : null;
  }
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return null;
};

const buildDateRangeToken = (id: string, value: unknown): string | null => {
  if (!Array.isArray(value)) return null;
  const [startDate, endDate] = value;
  const start = formatDateFilterToken(startDate);
  const end = formatDateFilterToken(endDate);
  if (!start || !end) return null;
  return `${id}:${start}~${end}`;
};

const buildFilterTokens = (filter: QueryLikeFilter): string[] => {
  const id = filter.id?.trim();
  if (!id) return [];

  if (isDateRangeFilterID(id)) {
    const dateRangeToken = buildDateRangeToken(id, filter.value);
    return dateRangeToken ? [dateRangeToken] : [];
  }

  const rawValues = Array.isArray(filter.value) ? filter.value : [filter.value];
  return rawValues
    .map((rawValue) => normalizeQueryFilterTokenValue(rawValue))
    .filter((value): value is string => Boolean(value))
    .map((value) => `${id}:${value}`);
};

export const buildClaimFiltersQueryString = (
  columnFilters: MRT_ColumnFiltersState
): string => {
  if (!columnFilters.length) return '';

  const filterTokens = columnFilters.flatMap((filter) =>
    buildFilterTokens(filter)
  );

  return filterTokens.join(',');
};

export const getClaimColumnFiltersFromSearchParams = (
  searchParams: URLSearchParams
): MRT_ColumnFiltersState | null => {
  const filtersQuery = searchParams.get('filters');
  if (filtersQuery !== null) {
    return parseClaimFiltersQueryString(filtersQuery);
  }

  return null;
};

const areFiltersEqual = (
  left: { id: string; value: unknown },
  right: { id: string; value: unknown }
): boolean => {
  const leftTokens = buildFilterTokens(left).sort();
  const rightTokens = buildFilterTokens(right).sort();
  if (leftTokens.length !== rightTokens.length) return false;
  return leftTokens.every((token, index) => token === rightTokens[index]);
};

export const hasAllClaimColumnFilters = (
  currentFilters: MRT_ColumnFiltersState,
  requiredFilters: MRT_ColumnFiltersState
): boolean => {
  if (!requiredFilters.length) return true;

  return requiredFilters.every((requiredFilter) =>
    currentFilters.some((currentFilter) =>
      areFiltersEqual(currentFilter, requiredFilter)
    )
  );
};

export const mergeClaimColumnFilters = (
  currentFilters: MRT_ColumnFiltersState,
  queryFilters: MRT_ColumnFiltersState
): MRT_ColumnFiltersState => {
  if (!queryFilters.length) return currentFilters;

  const overriddenIDs = new Set(queryFilters.map((filter) => filter.id));
  const preservedFilters = currentFilters.filter(
    (filter) => !overriddenIDs.has(filter.id)
  );

  return [...preservedFilters, ...queryFilters];
};
